const Vue = window.Vue;
const _ = window._;
const VUE_APP_ID = 'agent-websites-app';


/**
 * Vuex Store
 */
import { mapMutations, mapGetters, mapActions, mapState } from 'vuex';
import PropertySearchStore from '@vue/store/modules/property-search/store';

/**
 * Dependencies
 */
import { getDataObject, cleanMlsDisclaimer, tinyAdjust, handleAPIErrors, lightOrDark } from '@vue/store/helpers';
import { required, numeric } from 'vuelidate/lib/validators';
import { defaultZoomLevels } from '@vue/components/google-maps/constants/mapSettings';
import { submitAgentContactForm } from "@assets/js/api/agent-website";
import { getPosts } from '@assets/js/api/offices';

/**
 * Mixins
 */
import BaseStoreMixin from '@vue/mixins/base-store-mixin';
import PropertySearchMapMixin from '@vue/mixins/property-search-map-mixin';
import PropertySearchMixin from '@vue/mixins/property-search-mixin';

/**
 * Components
 */
import Listings from '@vue/components/agent-websites/Listings.vue';
import MarketResearch from '@vue/components/agent-websites/MarketResearch.vue';
import Testimonials from '@vue/components/agent-websites/Testimonials.vue';
import EducationCenter from '@vue/components/agent-websites/EducationCenter.vue';
import ContactAgent from '@vue/components/agent-websites/ContactAgent.vue';
import AboutAgent from '@vue/components/agent-websites/AboutAgent.vue';
import Pagination from '@vue/components/Pagination.vue';

if (document.getElementById(VUE_APP_ID)) {

  new Vue({
    el: '#' + VUE_APP_ID,

    store: PropertySearchStore,

    mixins: [
      BaseStoreMixin, 
      PropertySearchMixin, 
      PropertySearchMapMixin
    ],
    
    components: {
      Listings,
      MarketResearch,
      Testimonials,
      EducationCenter,
      ContactAgent,
      AboutAgent,
      Pagination
    },
        
    data: {
      appState: 'default',
      activeTab: 'listings',
      listingsInitialized: false,
      listingsLoading: true,
      educationCenterInitialized: false,
      educationCenterLoading: true,
      mobileNavShown: -1,
      resultsPerPage: 15,
      mapShown: -1,
      posts: [],
      headerShrunk: false,
      canEdit: false,
      mobileMode: false,
      colorLightnessValues: {
        primary: false,
        secondary: false,
        tertiary: false
      },

      contactFormLoading: false,
      contactFormSubmitted: false,
      
      contactForm: {
        email: '',
        phone: '',
        first_name: '',
        last_name: '',
        services: [],
        number_of_properties: '',
        timeline: 'twelve-months',
        comments: ''
      },
      agent: {},
      testimonials: [],
      waypoints: []
    },

    validations: {
        searchParams: {
          min_asking_price: { numeric },
          max_asking_price: { numeric }
        },
        contactForm: {
            email: { required },
            phone: { required },
            first_name: { required },
            last_name: { required },
            services: { required },
            number_of_properties: { required },
            timeline: { required }
        }
    },
    
    props: [],
    computed: {

      ...mapState('propertySearchStore', [
        'initialized',
        'listings',
        'properties',
        'searchParams',
        'selectedLocation',
        'mapConfig',
        'highlightedProperty',
        'mapView',
        'mlsNames',
        'mlsDisclaimersTextList',
        'brokerageFootersList',
        'searchView',
        'searchType',
        'mlsDisclaimerText',
        'mlsDisclaimers',
        'locationChanging',
        'totalCnt',
      ]),
      ...mapGetters('propertySearchStore', [
        'infoWindowPosition',
      ]),

      hasAgentPage(){
        return this.agent && this.agent.agent_page ? true : false;
      },
    
      numPages(){
        return Math.ceil(this.totalCnt / this.resultsPerPage);
      },
      
      firstPropertyElementId(){
        return (this.properties.length && this.properties[0].id) ? `property-card-${this.properties[0].id}` : '';
      },
      
      enabledSections(){
        if (!this.agent 
            || !this.agent.agent_page 
            || !this.agent.agent_page.flattened_sections
          ) {
            return [];
        }

        return this.agent.agent_page.flattened_sections.filter((section) => section.enabled == true);

      },

      primaryLicense(){
        if(!(this.agent && this.agent.agent_page && this.agent.agent_page.user.licenses.length)) return false;
        return this.agent.agent_page.user.licenses[0];
      },
      

      
      editUrl(){
        if(!this.agent || !this.user) return false;
        if(this.agent.id == this.user.id) {
          // the user is editing their own agent website
          return `/profile/agent-website`;
        } else if(this.canEdit){
          // the user is an admin who is editing someone else's agent website
          return `/user/${this.agent.id}/agent-website`;
        } else {
          return false;
        }
      },
      
      endingListingsIndex(){

        if(this.searchParams.page == 1) {
          return parseInt(this.searchParams.limit) > this.totalCnt ? parseInt(this.totalCnt) : parseInt(this.searchParams.limit);
        } else {
          return (parseInt(this.searchParams.page) * parseInt(this.searchParams.limit)) - 1
        }
        
      }
      
    },
    created() {
      const vm = this;
      vm.updateAppName(VUE_APP_ID);

      if(window.innerWidth < 992){
        this.mobileMode = true;
      }
    },

    mounted(){
      /**
       * Prevents layout issues if they refresh the page while scrolled down
       */
      window.scrollTo(0, top);
      const vm = this;
      this.updateSearchParams({limit: this.resultsPerPage});

      Promise.all([vm.getDataObject(), vm.fillInitialValues()])
        .then((response) => {

          const {agent, footer_txt} = response[0];
          const {listingsType, min_lat, max_lat, min_lng, max_lng, lng, lat } = response[1];

          let userHasAgentPage = agent && agent.agent_page;

          const selectedCounty = vm.hasAgentPage && vm.agent.agent_page.county ? vm.agent.agent_page.county : false;

          const newSearchParams = {};
          
          if(Math.abs(min_lat) && Math.abs(max_lat) && Math.abs(min_lng) && Math.abs(max_lng) && Math.abs(lat) && Math.abs(lng)){
            console.log(this.searchParams);
          } else if(
              selectedCounty
              && selectedCounty.name
              && selectedCounty.lat
              && selectedCounty.lng
            ){
              
              newSearchParams.location_txt = selectedCounty.name;
              newSearchParams.lat = selectedCounty.lat;
              newSearchParams.lng = selectedCounty.lng;

          }

          if (
              this.agent 
              && this.agent.agent_page 
              && this.agent.agent_page.flattened_sections 
              && Array.isArray(this.agent.agent_page.flattened_sections) 
              && this.agent.agent_page.flattened_sections.length) 
            {
              /**
               * Since we know there is at least one section, set the active tab in the navigation equal to the first enabled one
               */
              this.activeTab = this.agent.agent_page.flattened_sections.find(section => section.enabled).slug;

          }


          /**
           * Assign CSS variables to match the color scheme in the database
           */
          let colors = {};
          if(vm.hasAgentPage && agent.agent_page.primary_color) colors['agentSitePrimaryColor'] = agent.agent_page.primary_color;
          if(vm.hasAgentPage && agent.agent_page.secondary_color) colors['agentSiteSecondaryColor'] = agent.agent_page.secondary_color;
          vm.updateCSSVariables(colors)
            .then(() => {
              // 
              vm.appState = 'color-scheme-applied';
              vm.getLightOrDark(colors);
            })
            .catch(err => {
              console.warn(err);
            });
          
          /**
           * Change Listing type to match Agent Website's Focus
           */
          if(!listingsType && vm.hasAgentPage && agent.agent_page.focus == 'rent') {
            newSearchParams.listingsType = 'for-rent';
          }

          /**
           * Set a default market for the page
           */
          vm.selectedCounty = selectedCounty;

          /**
           * Commit new search params to store
           */
          vm.updateSearchParams(newSearchParams);
          

          /**
           * Commit map configuration to the store
           * - this causes the map to initialize, then idle. the idle event causes a new request for data to be sent to the API
           */
          vm.setupMapConfig(this.mobileMode)
            .then(res => {
              if(!this.mobileMode){
                let searchType = 'for-' + vm.agent.agent_page.focus;
                const zoom = defaultZoomLevels[searchType]['county'];
                vm.updateMapConfig({zoom});
              }
            })
            .catch(err => { 
              console.error(err); 
            });

          /**
           * Footer txt needed some formatting on the way in, it comes in separately
           */
          if(vm.agent.agent_page.office.footer_txt) {
            vm.agent.agent_page.office.footer_txt = footer_txt;
          }


          // let scrollingWrapper = document.querySelector('.agent-website__main');
          if(window.outerWidth >= 992){
            // vm.adjustLayoutHeights();
            // window.addEventListener('scroll', () => {
              
            //   _.once(vm.shrinkHeader);
            // });
          }

          vm.appState = 'initialized';

        })
        .catch(error => {
          console.warn(error);
        });
      
    },
    methods: {

      ...mapMutations('propertySearchStore', [
        'updateSelectedCounty',
        'updateSearchParams',
        'updateMapConfig',
        'updateMlsDisclaimerText',
        'updateInitialized',
        'updateListings',
        'updateTotalCnt',
        'updateMlsNames',
        'updateMlsPhotos',
        'updateHighlightedProperty',
        'clearHighlightedProperty',
        'updateMobileMode',
        'updateMarkers',
        'updateSelectedLocation',
        'updateMapConfigCenter',
        'updateMapConfigZoom',
      ]),
      ...mapActions('propertySearchStore', [
        'changeMapView',
        'changeSearchParams',
        'fillInitialValues',
        'getProperties',
        'getDisclaimer',
        'changeBoundsSearchParams',
      ]),

      adjustLayoutHeights(){


        let header = document.querySelector('.agent-website__header');
        let innerWrapper = document.querySelector('.agent-website-wrapper__inner');
        let headerHeight = (header.offsetHeight);
        let remainder = window.innerHeight - headerHeight;

        console.log('');
        console.log('adjustLayoutHeights');
        console.log({headerHeight, remainder});
        console.log('');

        innerWrapper.style.height = remainder + 'px';        
        
      },

      shrinkHeader(){
        const vm = this;
        return new Promise((resolve, reject) => {
          try {
            vm.disableScroll();;
            let header = document.querySelector('.agent-website__header');
            let innerWrapper = document.querySelector('.agent-website-wrapper__inner');
            let headerHeight = (header.offsetHeight);
            let remainder = window.innerHeight - headerHeight;

            console.log('');
            console.log('');
            console.log(headerHeight);
            console.log(remainder);
            console.log('');
            header.addEventListener('transitionend', (e) => {
              console.log('transition end');
              headerHeight = (header.offsetHeight);
              remainder = window.innerHeight - headerHeight;
              console.log(headerHeight);
              console.log(remainder);
              console.log('');
              console.log('');
              innerWrapper.style.height = remainder + 'px';
              vm.headerShrunk = false;
              this.enableScroll();
              resolve();
            });

            document.querySelector('body').style.overflow = 'hidden';
            header.classList.add('agent-website__header--shrink')
            console.log('transition start');

          } catch(e){
            reject();
          }
        });

      },

      /*
      --  Get Data Object --
      */
      getDataObject() {
        return new Promise((resolve, reject) => {
          try {

            const vm = this;
            let dataObject = getDataObject();
          
            if(dataObject.user){
              vm.updateUser(dataObject.user);
            }
            
            if (dataObject.storageDir) {
                vm.$store.commit('updateStorageDir', dataObject.storageDir);
            }
            
            if(dataObject.selected_user){
              vm.changeSelectedUser(dataObject.selected_user);
            }

            if(dataObject.agent){
              this.agent = dataObject.agent;
              /**
               * Do not use a default building type in For Rent searches
               * - This is here, because URL parameters are read in from the URL after this. If search parameters were passed in through the URL, they should override the defaults from vuex
               */
              if(dataObject.agent 
                && dataObject.agent.agent_page 
                && dataObject.agent.agent_page.focus == 'rent'){
                  vm.updateSearchParams({building_types: []})
              }
            };

            if(dataObject.canEdit){
              this.canEdit = dataObject.canEdit;
            };

            if(dataObject.selectedCounty){
              dataObject.selectedCounty = dataObject.selectedCounty;
            } else {
              vm.changeLoading(false);
            }

            resolve(dataObject);

          } catch(e){
            console.warn(e);
            reject(e);
          }
        });
      },
            
      onNavClick(tab){
        const vm = this;
      
        this.activeTab = tab;
        this.preventWaypointChange = true; // this is to prevent other waypoints from becoming the active tab just because they came into view during the process of scrolling the page to the chosen section

        this.onMobileNavToggle();
        this.changeActiveTab(tab);
        let id = 'as-section-' + tab;
        this.scrollToEl(id);

        /**
         * After a short duration, re-enable waypoints to automatically change the active tab
         */
        setTimeout(() => {
          console.log('re-enblihg waypoiints');
          this.preventWaypointChange = false;
        }, 1000);
        
      },

      onCmpMounted(cmp){

        console.log('component mounted: ', cmp);
        
      },
      
      onContactFormChange(params){
        this.contactForm = {...this.contactForm, ...params};
      },

      onContactFormSubmit(){
        this.$v.$touch();
        if(this.$v.$error) return false;

        this.contactFormLoading = true;

        submitAgentContactForm({ params: this.contactForm }, this.agent.id)
          .then(res => {

            this.contactFormSubmitted = true;

            swal({
              icon: 'success',
              title: 'Success',
              text: 'Your submission has been sent to the Agent. They will be in touch soon.'
            })
          })
          .catch(err => {
            handleAPIErrors(err);
          })
          .finally(() => {
            this.contactFormLoading = false;
          });

        // let GACLIENTID = false;
        //
        // ga(function(tracker) {
        //   GACLIENTID = tracker.get('clientId');
        // });
        //
        // let params = {
        //   ...this.contactForm,
        //   ...{
        //     office_id: this.agent.agent_page.office.id,
        //     address: this.agent.agent_page.office.street,
        //     city: this.agent.agent_page.office.city,
        //     state: this.agent.agent_page.office.state_cd,
        //     zipcode: this.agent.agent_page.office.zipcode,
        //     source: 'Agent',
        //     describe_agent: 'agent-website',
        //     agent_email: this.agent.email,
        //     GACLIENTID,
        //     GAUSERID: this.user && this.user.id || 'Unknown',
        //     GATRACKID: 'UA-27352266-1'
        //   }
        // };
        //
        // sendServicesSubmission({params})
        //   .then(res => {
        //
        //     this.contactFormSubmitted = true;
        //
        //     swal({
        //       icon: 'success',
        //       title: 'Success',
        //       text: 'Your submission has been sent to the Agent. They will be in touch soon.'
        //     })
        //   })
        //   .catch(err => {
        //     handleAPIErrors(err);
        //   })
        //   .finally(() => {
        //     this.contactFormLoading = false;
        //   });
      },

      /**
      * Handler for Selected Location changes. Checks to see if selected zipcodes exist, and if so goes thru the confirmation flow to prevnet them from accidentally being removed
      * @param {string} location A string that is in the format of the RW Location Code (loc_cd) {{location_level}}-{{location_id}}
      */
      onLocationSelectChange(location){

        this.locationChangeRequest(location);

      },

      onListingsTypeChange(type){

        const vm = this;
        
        this.preventWaypointChange = true;
        // this.changeActiveTab('listings');
        this.onFilterChange({listingsType: type});
        // this.scrollToEl('as-section-listings');

      },


      onFilterChange($event){

        this.changeSearchParams($event)
          .then(() => {
            this.loadNewProperties({resetPage: true});
          })
          .catch((error) => {
            console.warn(error);
          });
      },

      getTestimonials(){
        axios.get('/reviews')
          .then(res => {
            let testimonials = [];
            for(var index in res.data){
              testimonials.push(res.data[index]);
            }
            this.testimonials = testimonials;
          })
          .catch(err => {
            console.error(err);
          })
      },
      
      onMobileNavToggle(){
        this.mobileNavShown *= -1;
        document.querySelector('body').classList.toggle('mobile-nav-shown')
      },
      
      /**
       * Load New Properties into the Search App
       * @param {L} params 
       */
      loadNewProperties({initial = false, resetPage = false, scrollToTop = false}){
        const vm = this;
        console.log('');
        console.log('loadNewProperties');
        console.trace();
        console.log('');

        this.preventWaypointChange = true;
        
        if(resetPage){
          this.changeSearchParams({page: 1});
        }

        if(!vm.searchParams.lat || !vm.searchParams.lng) {
          console.log('-> backing ');
          vm.listingsLoading = false; 
          return false;
        }
        vm.listingsLoading = true;
        vm.updateMlsDisclaimerText('These listings are courtesy of RentersWarehouse.com');
        let requestParams = {
          initial: initial || false,
          query: {}
        };

        if(this.searchParams.listingsView == 'agent'){
          requestParams.query.partner_id = this.agent.id;
        } else if(this.searchParams.listingsType == 'for-rent' && this.searchParams.listingsView == 'all') {
          requestParams.query.office_id = this.agent.agent_page.office.id;
        }
        
        this.getProperties(requestParams)
          .then((
            {
              properties, 
              total_cnt,
              mls_photos,
              mls
            }
          ) => {
            vm.updateListings(properties);
            if(scrollToTop){
              vm.scrollResultsToTop();
            }
            // if(params.includeMarkers == undefined || params.includeMarkers == true){ vm.updateMarkers(markers); }
            vm.updateMarkers(properties);
            vm.updateTotalCnt(total_cnt);
            vm.updateMlsPhotos(mls_photos);

            // setTimeout(() => {
            //   vm.initializeWaypoints();
            // }, 500)
            

            if(vm.searchParams.listingsType == 'for-sale'){

              /**
               * MLS Disclaimer functionality
               */
              if(mls){
                vm.updateMlsNames([mls]);
              }

              // if(vm.mlsDisclaimers[mls]){
              //   vm.updateMlsDisclaimerText(vm.mlsDisclaimers[mls]);
              // } else {
              //   vm.getDisclaimer(mls)
              //     .then(res => {
              //       vm.updateMlsDisclaimerText(res);
              //     });
              // }
              
            }
            this.listingsLoading = false;
            if(!vm.listingsInitialized) {
              vm.listingsInitialized = true;
            }
            setTimeout(() => {
              /** give tiles time to enter the dom */
              vm.preventWaypointChange = false;
            }, 500)
            window.initOATooltips();
            cleanMlsDisclaimer();

            /**
             * Special behaviors that only happen on the first time through this function
             */
            if(initial){
              vm.isFirstRequest = false;

              if(!properties.length && vm.searchParams.listingsView == 'agent'){
                vm.onFilterChange({listingsView: 'all'});
              }
              
            }


          })
          .catch((err) => {
            let response = err.response;
            /**
             * return app state to whatever it was when we started this request
             */
            vm.listingsLoading = false;
            vm.listingsInitialized = true;
            vm.preventWaypointChange = false;
            if(response && response.status == 500) {
              let alertText = window.location.href.indexOf('renterswarehouse.com') !== -1 
                ? 'Internal Server Error. Please contact support'
                : 'Message: ' + response.data.message;
              swal({
                title: '500: Internal Server Error',
                icon: 'error',
                text: alertText,
                // timer: 2000,
                // showConfirmButton: false
              });
            } else {
              console.error('');
              console.error('Error');
              console.error(err);
              console.error(err.response);
              console.error('');
            }
          });
      },
      
      
      onToggleMap(){
        this.changeMapView();
      },

      handleWaypointEnter(section){
        // console.log('');
        // console.log('handleWaypointEnter', section);
        if(this.preventWaypointChange) {
          console.log('changing waypoints (' + section + '), backing out..')
          return false;
        }
        this.changeActiveTab(section);
        console.log('');
      },
      
      changeActiveTab(tab){
        // console.log('');
        // console.log('agent-website.js :: changeActiveTab', tab);
        // console.log('');
        // console.log('changing active tab: ', tab);
        if(!tab) {
          console.warn('agent-website.js :: changeActiveTab :: invalid tab name');
          return false;
        }
        this.activeTab = tab;
      },

      scrollToEl(id){
        let header = document.querySelector('.agent-website__header');
        var top = document.getElementById(id).offsetTop; //Getting Y of target element
        /**
         * Top top offset is calculated from the top of the viewport, not the distance from the top of the scrollable container. need to subtract the height of the header. 
         * We should probably take the height of the viewport and subtract the height of the scrollable container from it instead.
         */
        top -= header.clientHeight;
        
        if(window.outerWidth >= 992){
          console.log('');
          console.log('scrollToEl');
          console.log(id);
          console.log('');
          document.querySelector('.agent-website__main').scrollTo(0, top); 

        } else {
          console.log('scrolling to ', top);
          window.scrollTo(0, top);
        }
          
      },

      scrollResultsToTop(){
        this.scrollToEl('as-section-listings');
      },


      
      initializeWaypoints(){
        console.log('initializing waypoints');
        const vm = this;
        let sectionNames = [
          'listings',
          'market-research',
          'testimonials',
          'education-center',
          'contact-agent',
          'about-agent',
        ];
        sectionNames.map((section, index) => {
          let el = document.querySelector(`.as-section[data-section="${section}"]`);
          if(!el){
            console.warn('agent-website.js :: initializeWaypoints :: invalid section name', section);
          } else {
            if(this.waypoints.includes(section)){
              // vm.$removeObserver(el);
              this.waypoints.splice(this.waypoints.indexOf(section), 1);
  
            } else {
              vm.$addObserver(el, vm.onWaypointEnter, vm.intersectionOptions);
              this.waypoints.push(section);
            }
          }
        })
      },
      
      getOfficePosts(){
        this.educationCenterLoading = true;
        getPosts({officeSlug: this.agent.agent_page.office.slug})
          .then(res => {
            this.posts = res.data;
            this.educationCenterLoading = false;
            this.educationCenterInitialized = true;
          })
          .catch(err => {
            console.error(err);
            this.educationCenterLoading = false;
          })
      },
      
      goToPage(page){
        this.changeLoading(true);
        this.scrollResultsToTop();
        this.changeSearchParams({page})
          .then(() => {
            // console.log($event, response);
            this.loadNewProperties({});
          })
          .catch((error) => {
            console.warn(error);
          });
      },
    

      /**
       * Set CSS variables for each property in the passed-in colors object
       * @param {object} colors Colors object from the database
       */
      updateCSSVariables(colors) {
        return new Promise((resolve, reject) => {
          try {
            if (!colors) {
              reject('invalid colors');
              return false;
            }
            let root = document.documentElement;
            const interval = 5;
            const rows = 21;
            const end = Math.floor(rows / 2) * interval;
            colors = Object.entries(colors);
            colors.map(color => {
              let colorName = color[0];
              let hexValue = color[1];
              root.style.setProperty('--' + colorName, hexValue);



              for(let diff = (end * -1); diff < end; diff += interval){
                // console.log(diff);
                let value = hexValue;
                let action = false;
                if(diff > 0){
                  action = 'lighten';
                } else if(diff < 0){
                  action = 'darken';
                }
                if(action){
                  value = tinyAdjust(hexValue, action, Math.abs(diff));
                }
                let colorVarName = `${colorName}_${diff}`;
                root.style.setProperty('--' + colorVarName, value);
              }



            })
            if(colors.length){
              /**
              * Some CSS relies on this element having this class - for example the Tooltips which get placed at the document's root
              */
              document.querySelector('body').classList.add('layout-agent-websites--has-colors');
            } 
            resolve();
          } catch(e){
            reject(e);
          }
        });

      },
      
      onMarkerClick(property){
        console.log(property);
        if(!property || !property.id) { console.warn('onMarkerClick ::invalid property'); return false; }
        window.sendClickEvent({
          category: 'Behavior Tracking - Search - Marker Click',
          action: this.searchParams.listingsType
        });
        if(this.searchParams.listingsType == 'for-sale'){
          window.open(
            `/mls-listings/${property.id}`,
            '_blank' // <- This is what makes it open in a new window.
          );
          return false;
        } else {
          window.open(
            `/rental-listings/${property.id}`,
            '_blank' // <- This is what makes it open in a new window.
          );
          return false;
        }
      },

      disableScroll() { 
          var scrollTop, scrollLeft;
          // Get the current page scroll position 
          scrollTop = window.pageYOffset || document.documentElement.scrollTop; 
          scrollLeft = window.pageXOffset || document.documentElement.scrollLeft, 

              // if any scroll is attempted, set this to the previous value 
              window.onscroll = function() { 
                  window.scrollTo(scrollLeft, scrollTop); 
              }; 
      },

      enableScroll() { 
          window.onscroll = function() {}; 
      },
      
      getLightOrDark(colors){
        let primary = colors.agentSitePrimaryColor;
        let secondary = colors.agentSiteSecondaryColor;

        if(primary) {
          this.colorLightnessValues.primary = lightOrDark(primary, 60);
        }
        if(secondary) {
          this.colorLightnessValues.secondary = lightOrDark(secondary, 60);
        }
        
      }
      
    },
    watch: {},
  });

}
